import {
  AgentCheckpointConfig,
  AgentImageChoice,
  CheckpointConfig,
  CheckpointType,
  ConnectorConfig,
  DistributorConfig,
  GeneratorConfig,
  MediaAttachment,
  OutputField,
  PipelineInputAttachment,
  PipelineOutputFormat,
  PipelineRun,
  PipelineTemplate,
  PromptCheckpointConfig,
  SequencerConfig,
  ToolCheckpointConfig,
} from './structs';
import { cleanString, isRecord, toNumberValue, toStringValue } from './typeHelpers';

const hasOwn = <T extends object>(value: T, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const normalizePositiveInteger = (value: unknown): number | undefined => {
  const parsed = toNumberValue(value);
  if (parsed === undefined || parsed <= 0) return undefined;
  return Math.floor(parsed);
};

const sanitizeSettingsMap = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;

  const next: Record<string, unknown> = {};
  Object.entries(value).forEach(([rawKey, rawValue]) => {
    const key = toStringValue(rawKey).trim();
    if (!key || rawValue === undefined || rawValue === null) return;
    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      if (!trimmed) return;
      next[key] = trimmed;
      return;
    }
    next[key] = rawValue;
  });

  return Object.keys(next).length > 0 ? next : undefined;
};

const sanitizeInputMapping = (
  mapping: CheckpointConfig['input_mapping']
): CheckpointConfig['input_mapping'] => {
  const next: Record<string, string> = {};
  Object.entries(mapping || {}).forEach(([rawKey, rawValue]) => {
    const key = toStringValue(rawKey).trim();
    const value = toStringValue(rawValue).trim();
    if (!key || !value) return;
    next[key] = value;
  });
  return next;
};

const sanitizeRequiredAssets = (
  requiredAssets: CheckpointConfig['required_assets']
): CheckpointConfig['required_assets'] => {
  if (!Array.isArray(requiredAssets) || requiredAssets.length === 0) return undefined;

  // A required asset has exactly one canonical wiring: `source: "checkpoint:<id>"`
  // ("which prior checkpoint provides this asset"). Legacy shapes — `checkpoint`
  // + `checkpoint_id`, or the pre-collapse `user`/`initial`/`any`/`upload` sources
  // paired with a `checkpoint_id` — are rewritten here so old templates display and
  // round-trip in the collapsed form (the backend normalizes identically).
  // `checkpoint_id` is still emitted alongside for engine compat.
  const LEGACY_CHECKPOINT_SOURCES = new Set(['checkpoint', 'user', 'initial', 'any', 'upload']);

  const toCheckpointSource = (
    source?: string,
    checkpointId?: string
  ): { source?: string; checkpoint_id?: string } => {
    const cleanedSource = cleanString(source);
    const cleanedCheckpointId = cleanString(checkpointId);
    const normalizedSource = (cleanedSource || '').toLowerCase();

    if (normalizedSource.startsWith('checkpoint:')) {
      const sourceCheckpointId = cleanString(cleanedSource?.slice('checkpoint:'.length));
      return sourceCheckpointId
        ? { source: `checkpoint:${sourceCheckpointId}`, checkpoint_id: sourceCheckpointId }
        : { source: cleanedSource, checkpoint_id: cleanedCheckpointId };
    }

    if (cleanedCheckpointId && (!normalizedSource || LEGACY_CHECKPOINT_SOURCES.has(normalizedSource))) {
      return {
        source: `checkpoint:${cleanedCheckpointId}`,
        checkpoint_id: cleanedCheckpointId,
      };
    }

    return { source: cleanedSource, checkpoint_id: cleanedCheckpointId };
  };

  const sanitized = requiredAssets
    .map((asset) => {
      const key = cleanString(asset?.key);
      const type = cleanString(asset?.type);
      const { source, checkpoint_id: checkpointId } = toCheckpointSource(
        asset?.source,
        asset?.checkpoint_id
      );
      const mediaId = cleanString(asset?.media_id);

      if (!key && !type && !source && !checkpointId && !mediaId) {
        return null;
      }

      return {
        key,
        type,
        source,
        checkpoint_id: checkpointId,
        media_id: mediaId,
      };
    })
    .filter((asset): asset is NonNullable<typeof asset> => Boolean(asset));

  return sanitized.length > 0 ? sanitized : undefined;
};

const normalizeAttachment = (value: unknown, index: number): MediaAttachment | null => {
  if (!isRecord(value)) return null;

  const url = toStringValue(value.url ?? value.file_url ?? value.asset_url ?? value.uri);
  const id =
    toStringValue(value.id ?? value.attachment_id ?? value.asset_id) ||
    (url ? `attachment:${url}` : `attachment-${index}`);
  if (!id) return null;

  const size = toNumberValue(value.size ?? value.size_bytes ?? value.sizeBytes);
  // Treat empty/whitespace candidates as missing so the chain falls through to the
  // indexed fallback (backend can emit `filename: ""`, which `??` would not skip).
  const name =
    cleanString(value.name) ??
    cleanString(value.filename) ??
    cleanString(value.file_name) ??
    cleanString(value.title) ??
    `attachment-${index + 1}`;

  return {
    id,
    media_id: cleanString(value.media_id ?? value.mediaId),
    role: cleanString(value.role),
    scene_id: cleanString(value.scene_id ?? value.sceneId),
    frame_order: normalizePositiveInteger(value.frame_order ?? value.frameOrder),
    type: toStringValue(value.type ?? value.media_type ?? value.kind, 'unknown'),
    url,
    mime_type: toStringValue(
      value.mime_type ?? value.mimeType ?? value.content_type,
      'application/octet-stream'
    ),
    name,
    filename: cleanString(value.filename ?? value.file_name ?? value.fileName ?? name),
    created_at: toStringValue(value.created_at ?? value.createdAt, new Date(0).toISOString()),
    source: cleanString(value.source),
    size_bytes: size,
    size,
    metadata: isRecord(value.metadata) ? value.metadata : undefined,
    checkpoint_id: cleanString(value.checkpoint_id ?? value.checkpointId),
    checkpoint_index: toNumberValue(value.checkpoint_index ?? value.checkpointIndex),
    source_checkpoint_id: cleanString(value.source_checkpoint_id ?? value.sourceCheckpointId),
    source_run_id: cleanString(value.source_run_id ?? value.sourceRunId),
    generated_from_checkpoint_id: cleanString(
      value.generated_from_checkpoint_id ?? value.generatedFromCheckpointId
    ),
    generated_from_field: cleanString(value.generated_from_field ?? value.generatedFromField),
  };
};

const normalizeAttachmentList = (value: unknown): MediaAttachment[] | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return [];
  if (!Array.isArray(value)) return [];

  return value
    .map((attachment, index) => normalizeAttachment(attachment, index))
    .filter((attachment): attachment is MediaAttachment => Boolean(attachment));
};

const normalizeSeedImageMode = (generator: Record<string, unknown>): GeneratorConfig['mode'] => {
  const explicitMode = cleanString(generator.mode);
  if (explicitMode) return explicitMode;

  const role = toStringValue(generator.role).trim().toLowerCase();
  const legacySeedRoles = new Set([
    'reference_image',
    'seed_image',
    'character_seed',
    'reference',
  ]);
  return legacySeedRoles.has(role) ? 'image_to_image' : 'text_to_image';
};

const normalizePromptGate = (
  value: unknown,
  fallbackProvider?: string,
  fallbackModel?: string
): PromptCheckpointConfig | undefined => {
  const source = isRecord(value) ? value : {};
  const provider = cleanString(source.provider ?? fallbackProvider);
  const model = cleanString(source.model ?? fallbackModel);
  if (!provider && !model) return undefined;
  return { provider, model };
};

const normalizeDistributor = (
  value: unknown,
  fallbackProvider?: string,
  fallbackModel?: string
): DistributorConfig => {
  const source = isRecord(value) ? value : {};
  const distributor: DistributorConfig = {
    provider: cleanString(source.provider ?? fallbackProvider),
    model: cleanString(source.model ?? fallbackModel),
    max_children: normalizePositiveInteger(source.max_children) ?? 1,
    // Dropping this on round-trip silently broke the per-run scene-count
    // override for every template saved through the builder.
    max_children_param: cleanString(source.max_children_param ?? source.maxChildrenParam),
    prompt_decorator: cleanString(source.prompt_decorator ?? source.promptDecorator),
  };
  // The fan-out source ("checkpoint:<id>.<field>"). Dropping it turns a
  // deterministic fan-out into a planner call on the first save. Only a set
  // value is carried, so existing distributors save as they were.
  const fanOutSource = cleanString(source.source);
  if (fanOutSource) distributor.source = fanOutSource;
  return distributor;
};

/**
 * Sequential distributor block. `max_steps` keeps its literal value (0 means
 * "unset" — the validator flags it) and `allowed_parent_refs` is carried
 * through verbatim as the name → source map the engine reads.
 */
const normalizeSequencer = (
  value: unknown,
  fallbackProvider?: string,
  fallbackModel?: string
): SequencerConfig => {
  const source = isRecord(value) ? value : {};
  const refsSource = isRecord(source.allowed_parent_refs ?? source.allowedParentRefs)
    ? ((source.allowed_parent_refs ?? source.allowedParentRefs) as Record<string, unknown>)
    : undefined;
  const allowedParentRefs: Record<string, string> = {};
  Object.entries(refsSource || {}).forEach(([rawKey, rawValue]) => {
    const key = toStringValue(rawKey).trim();
    const mapped = toStringValue(rawValue).trim();
    if (!key || !mapped) return;
    allowedParentRefs[key] = mapped;
  });

  // Same carry-through as allowed_parent_refs: this normalizer is a whitelist,
  // so a field it does not name is DELETED from every template that passes
  // through — which is how the judge's story descriptions would silently
  // vanish on the first save from the builder UI.
  const descriptionsSource = isRecord(source.ref_descriptions ?? source.refDescriptions)
    ? ((source.ref_descriptions ?? source.refDescriptions) as Record<string, unknown>)
    : undefined;
  const refDescriptions: Record<string, string> = {};
  Object.entries(descriptionsSource || {}).forEach(([rawKey, rawValue]) => {
    const key = toStringValue(rawKey).trim();
    const description = toStringValue(rawValue).trim();
    if (!key || !description) return;
    refDescriptions[key] = description;
  });

  return {
    provider: cleanString(source.provider ?? fallbackProvider),
    model: cleanString(source.model ?? fallbackModel),
    max_steps: normalizePositiveInteger(source.max_steps ?? source.maxSteps) ?? 0,
    max_steps_param: cleanString(source.max_steps_param ?? source.maxStepsParam),
    allowed_parent_refs:
      Object.keys(allowedParentRefs).length > 0 ? allowedParentRefs : undefined,
    ref_descriptions:
      Object.keys(refDescriptions).length > 0 ? refDescriptions : undefined,
  };
};

const normalizeConnector = (value: unknown): ConnectorConfig => {
  const source = isRecord(value) ? value : {};
  return {
    strategy: 'collect_all',
    source_checkpoint_id: cleanString(source.source_checkpoint_id ?? source.sourceCheckpointId),
  };
};

const normalizeGenerator = (
  value: unknown,
  fallbackProvider?: string,
  fallbackModel?: string
): GeneratorConfig => {
  const source = isRecord(value) ? value : {};
  const generator: GeneratorConfig = {
    media_type: toStringValue(source.media_type).trim() || 'image',
    role: cleanString(source.role),
    mode: normalizeSeedImageMode(source),
    provider: cleanString(source.provider ?? source.generator ?? fallbackProvider),
    model: cleanString(source.model ?? fallbackModel),
  };
  // This rebuild is a WHITELIST: a field it does not name is deleted from every
  // template that passes through, on read AND on save. Dropping closing_frame
  // meant the Builder's own output arrived without it and a ticked checkbox
  // unchecked itself on save, because the flag never reached the server.
  // Only `true` is carried: the flag defaults to off, so persisting `false`
  // would write noise into every generator that never wanted one.
  if ((source.closing_frame ?? source.closingFrame) === true) {
    generator.closing_frame = true;
  }
  // Which locked video model this generator runs on. Dropped, the crew's two
  // video generators would fall back to the template's placeholder models.
  // Carried as authored (trimmed) so an unknown value reaches the server's
  // check instead of vanishing; an empty one is not carried.
  const videoMode = cleanString(source.video_mode ?? source.videoMode);
  if (videoMode) generator.video_mode = videoMode;
  return generator;
};

const normalizeToolConfig = (value: unknown): ToolCheckpointConfig => {
  const source = isRecord(value) ? value : {};
  const tool: ToolCheckpointConfig = { name: cleanString(source.name) || '' };
  const timeout = normalizePositiveInteger(source.timeout_s ?? source.timeoutS);
  if (timeout !== undefined) tool.timeout_s = timeout;
  return tool;
};

// An agent's own image generator: trimmed provider/model and a settings map
// cleaned like an output_spec; undefined when nothing is left, so an empty
// block never reaches the save.
const normalizeAgentImage = (value: unknown): AgentImageChoice | undefined => {
  if (!isRecord(value)) return undefined;
  const image: AgentImageChoice = {};
  const provider = cleanString(value.provider);
  const model = cleanString(value.model);
  const settings = sanitizeSettingsMap(value.settings);
  if (provider) image.provider = provider;
  if (model) image.model = model;
  if (settings) image.settings = settings;
  return Object.keys(image).length > 0 ? image : undefined;
};

// The editor's agent form edits only the model, the image generator and the
// limits, so the block is carried as authored — the fields the engine reads,
// including an inline output_schema and the shot_list check, cleaned enough
// that a save cannot send a non-array allowlist or a non-object limit block.
const normalizeAgentConfig = (value: unknown): AgentCheckpointConfig => {
  const source = isRecord(value) ? value : {};
  const agent: AgentCheckpointConfig = {
    prompt_template_id: cleanString(source.prompt_template_id ?? source.promptTemplateId) || '',
    tools: Array.isArray(source.tools)
      ? source.tools
          .filter((tool): tool is string => typeof tool === 'string' && tool.trim() !== '')
          .map((tool) => tool.trim())
      : [],
    output_contract: cleanString(source.output_contract ?? source.outputContract) || '',
  };
  // A saved director recipe's agents carry their instructions inline. Both
  // fields are carried as authored (prompt_template_id stays '' beside an
  // objective, as the backend emits it) so a template with both reaches the
  // validator, which names the rule, instead of one of them vanishing.
  const objective = cleanString(source.objective);
  if (objective) agent.objective = objective;
  const outputSchema = source.output_schema ?? source.outputSchema;
  if (isRecord(outputSchema)) agent.output_schema = outputSchema as Record<string, unknown>;
  if (isRecord(source.limits)) agent.limits = source.limits as AgentCheckpointConfig['limits'];
  if (isRecord(source.model)) agent.model = source.model as AgentCheckpointConfig['model'];
  const image = normalizeAgentImage(source.image);
  if (image) agent.image = image;
  // The finish checks. Dropped here, saving the crew in the pipeline editor
  // would silently turn them off. An empty block is carried too, as authored,
  // so the server names what is missing instead of it vanishing.
  if (isRecord(source.shot_list)) {
    const script = cleanString(source.shot_list.script);
    agent.shot_list = script ? { script } : {};
  }
  return agent;
};

const normalizeCheckpointConfig = (checkpoint: unknown): CheckpointConfig => {
  const source = isRecord(checkpoint) ? checkpoint : {};
  const rawType = cleanString(source.type);
  const type: CheckpointType =
    rawType === 'distributor' ||
    rawType === 'connector' ||
    rawType === 'generator' ||
    rawType === 'upload' ||
    rawType === 'sequencer' ||
    rawType === 'tool' ||
    rawType === 'agent' ||
    rawType === 'prompt'
      ? rawType
      : 'prompt';
  const legacyProvider = cleanString(source.provider);
  const legacyModel = cleanString(source.model);

  const rawChainConnections = source.chain_connections ?? source.chainConnections;
  const chainConnections: CheckpointConfig['chain_connections'] = Array.isArray(rawChainConnections)
    ? (rawChainConnections as CheckpointConfig['chain_connections'])
    : undefined;

  const normalized: CheckpointConfig = {
    id: toStringValue(source.id).trim(),
    name: cleanString(source.name) || toStringValue(source.id).trim() || 'Checkpoint',
    type,
    prompt_template_id: toStringValue(source.prompt_template_id ?? source.promptTemplateId).trim(),
    input_mapping: sanitizeInputMapping(
      isRecord(source.input_mapping) ? (source.input_mapping as Record<string, string>) : {}
    ),
    requires_confirm: Boolean(source.requires_confirm ?? source.requiresConfirm),
    chain_last_frames: Boolean(source.chain_last_frames ?? source.chainLastFrames),
    chain_connections: chainConnections,
    output_spec: sanitizeSettingsMap(source.output_spec ?? source.outputSpec),
    required_assets: sanitizeRequiredAssets(
      Array.isArray(source.required_assets)
        ? (source.required_assets as CheckpointConfig['required_assets'])
        : undefined
    ),
    // The closing frame's own wiring. It goes through the same sanitizer, which
    // leaves "self:opening_frame" alone: it carries no checkpoint id and is not
    // a legacy source, so the checkpoint-source rewrite never touches it.
    closing_assets: sanitizeRequiredAssets(
      Array.isArray(source.closing_assets)
        ? (source.closing_assets as CheckpointConfig['closing_assets'])
        : undefined
    ),
  };

  if (type === 'prompt') {
    normalized.promptGate = normalizePromptGate(source.promptGate, legacyProvider, legacyModel);
  }

  if (type === 'distributor') {
    normalized.distributor = normalizeDistributor(source.distributor, legacyProvider, legacyModel);
  }

  if (type === 'sequencer') {
    normalized.sequencer = normalizeSequencer(source.sequencer, legacyProvider, legacyModel);
  }

  if (type === 'connector') {
    normalized.connector = normalizeConnector(source.connector);
  }

  if (type === 'generator') {
    normalized.generator = normalizeGenerator(source.generator, legacyProvider, legacyModel);
  }

  if (type === 'upload') {
    const uploadSource = isRecord(source.upload) ? source.upload : {};
    normalized.upload = {
      media_type: cleanString(uploadSource.media_type) || 'image',
      role: cleanString(uploadSource.role) || 'seed_image',
    };
  }

  // This rebuild is a whitelist: without these two, every load turned a tool
  // checkpoint into a prompt and every save deleted the tool and the contract.
  if (type === 'tool') {
    normalized.tool = normalizeToolConfig(source.tool);
  }
  // The same whitelist would turn an agent checkpoint into a prompt and drop
  // its agent block on every load and save.
  if (type === 'agent') {
    normalized.agent = normalizeAgentConfig(source.agent);
  }
  // Carried on any type so a misplaced contract reaches the validator (which
  // names the rule) instead of vanishing; applyCheckpointType clears it when a
  // checkpoint stops being a prompt.
  const outputContract = cleanString(source.output_contract ?? source.outputContract);
  if (outputContract) {
    normalized.output_contract = outputContract;
  }
  const renderStyle = cleanString(source.render_style ?? source.renderStyle);
  if (renderStyle) {
    normalized.render_style = renderStyle;
  }
  // output_fields (UD-3): carried like output_contract, on any type, so a
  // misplaced one reaches the validator; dropping it would delete the
  // engine's check of the reply from any template saved through the UI.
  const outputFieldsSource = source.output_fields ?? source.outputFields;
  if (isRecord(outputFieldsSource) && Object.keys(outputFieldsSource).length > 0) {
    const outputFields: Record<string, OutputField> = {};
    Object.entries(outputFieldsSource).forEach(([name, raw]) => {
      const field = isRecord(raw) ? raw : {};
      outputFields[name] = {
        ...(field.required === true ? { required: true } : {}),
        // AV-1: without its type a "string_list" field reads back as a string,
        // and the engine refuses every list reply to it.
        ...(typeof field.type === 'string' && field.type ? { type: field.type } : {}),
        ...(typeof field.pattern === 'string' && field.pattern ? { pattern: field.pattern } : {}),
        ...(typeof field.format === 'string' && field.format ? { format: field.format } : {}),
      };
    });
    normalized.output_fields = outputFields;
  }

  // The optional AI-review block and the canvas_bound flag are pure
  // passthrough: both are checkpoint-level settings the editor never edits
  // field-by-field, and dropping them here silently deleted them from every
  // template saved through the UI.
  if (isRecord(source.review)) {
    normalized.review = source.review as CheckpointConfig['review'];
  }
  // Same passthrough, same reason — and this whitelist has now eaten two
  // features (closing_frame above, then this one). A discussion block dropped
  // here made the toggle untickable: it set state, never reached the server,
  // and read back as off. Dropping it on LOAD also hid the "Open the
  // discussion" entry point, so a run parked with no way into the conversation.
  // Carried only when `input` is actually present: it is the one required
  // field, and a block without it is malformed rather than merely partial —
  // passing it through would hand the editor a config it cannot render and the
  // server one it will reject.
  if (isRecord(source.discussion) && typeof source.discussion.input === 'string') {
    normalized.discussion = {
      ...(source.discussion as Record<string, unknown>),
      input: source.discussion.input,
    } as CheckpointConfig['discussion'];
  }
  const canvasBound = source.canvas_bound ?? source.canvasBound;
  if (typeof canvasBound === 'boolean') {
    normalized.canvas_bound = canvasBound;
  }
  // A held gate stays held in auto mode. Dropped, a series episode (auto_mode)
  // would run straight through the frames gate into paid video. Only `true` is
  // carried (the flag defaults off), on any type so a misplaced one reaches the
  // server's check rather than vanishing.
  if ((source.hold_in_auto_mode ?? source.holdInAutoMode) === true) {
    normalized.hold_in_auto_mode = true;
  }

  // Preserve skip_when (Go pipeline.SkipCondition) in either form — dropping
  // it here would silently delete the skip rule from any template saved via
  // the UI. A rule naming both forms is kept whole for the validator to report.
  const skipWhenSource = source.skip_when ?? source.skipWhen;
  if (isRecord(skipWhenSource)) {
    const param = cleanString(skipWhenSource.param);
    const checkpoint = cleanString(skipWhenSource.checkpoint);
    if (param || checkpoint) {
      normalized.skip_when = {
        ...(param ? { param } : {}),
        ...(checkpoint ? { checkpoint } : {}),
        equals: toStringValue(skipWhenSource.equals),
      };
    }
  }

  return normalized;
};

const normalizeCheckpointList = (value: unknown): CheckpointConfig[] => {
  if (!Array.isArray(value)) return [];
  return value.map((checkpoint) => normalizeCheckpointConfig(checkpoint));
};

export const normalizePipelineOutputFormat = (
  value: unknown
): PipelineOutputFormat | undefined => {
  if (!isRecord(value)) return undefined;

  const next: PipelineOutputFormat = {
    image_provider: cleanString(value.image_provider ?? value.imageProvider),
    image_model: cleanString(value.image_model ?? value.imageModel),
    image_settings: sanitizeSettingsMap(value.image_settings ?? value.imageSettings),
    video_provider: cleanString(value.video_provider ?? value.videoProvider),
    video_model: cleanString(value.video_model ?? value.videoModel),
    video_settings: sanitizeSettingsMap(value.video_settings ?? value.videoSettings),
    audio_provider: cleanString(value.audio_provider ?? value.audioProvider),
    audio_model: cleanString(value.audio_model ?? value.audioModel),
    audio_settings: sanitizeSettingsMap(value.audio_settings ?? value.audioSettings),
  };

  return Object.values(next).some((entry) => entry !== undefined) ? next : undefined;
};

export const serializePipelineOutputFormat = (
  value?: PipelineOutputFormat
): PipelineOutputFormat | undefined => normalizePipelineOutputFormat(value);

export const serializeCheckpointConfig = (checkpoint: CheckpointConfig): CheckpointConfig => {
  const normalized = normalizeCheckpointConfig(checkpoint);

  if (normalized.type === 'prompt' && normalized.promptGate) {
    normalized.promptGate = normalizePromptGate(normalized.promptGate);
  }

  if (normalized.type === 'distributor' && normalized.distributor) {
    normalized.distributor = normalizeDistributor(normalized.distributor);
  }

  if (normalized.type === 'sequencer' && normalized.sequencer) {
    normalized.sequencer = normalizeSequencer(normalized.sequencer);
  }

  if (normalized.type === 'connector' && normalized.connector) {
    normalized.connector = normalizeConnector(normalized.connector);
  }

  if (normalized.type === 'generator' && normalized.generator) {
    normalized.generator = normalizeGenerator(normalized.generator);
  }

  return normalized;
};

export const serializeCheckpointList = (checkpoints: CheckpointConfig[]): CheckpointConfig[] =>
  checkpoints.map((checkpoint) => serializeCheckpointConfig(checkpoint));

export const normalizePipelineTemplate = (template: unknown): PipelineTemplate => {
  const source = isRecord(template) ? template : {};
  const normalized: PipelineTemplate = {
    ...(template as PipelineTemplate),
    id: toStringValue(source.id),
    name: toStringValue(source.name),
    description: toStringValue(source.description),
    output_format: normalizePipelineOutputFormat(source.output_format ?? source.outputFormat),
    parameters: isRecord(source.parameters)
      ? (source.parameters as PipelineTemplate['parameters'])
      : undefined,
    checkpoints: normalizeCheckpointList(source.checkpoints),
    version: toNumberValue(source.version) ?? 1,
    user_id: cleanString(source.user_id ?? source.userId),
    created_at: toStringValue(source.created_at ?? source.createdAt),
    updated_at: toStringValue(source.updated_at ?? source.updatedAt),
    // Opaque passthrough — the backend never validates its contents, and
    // neither do we. Only guard against a non-object value slipping through
    // (e.g. a stray string/number); anything object-shaped, including an
    // unrecognized future shape, is carried through untouched.
    ui: isRecord(source.ui) ? (source.ui as PipelineTemplate['ui']) : undefined,
    // Same treatment as `ui`: whole-object passthrough guarded only against a
    // non-object value. Field-level shape is enforced by validatePipeline.ts,
    // not here.
    review_defaults: isRecord(source.review_defaults)
      ? (source.review_defaults as PipelineTemplate['review_defaults'])
      : undefined,
  };
  // The marker that binds a template to the user's locked generation settings
  // ("user"). The spread above already carries it; this settles what is saved:
  // a trimmed value as authored, and no key at all when it is empty.
  const generationSettings = cleanString(source.generation_settings);
  if (generationSettings) normalized.generation_settings = generationSettings;
  else delete normalized.generation_settings;
  return normalized;
};

export const normalizePipelineRun = (run: unknown): PipelineRun => {
  if (!isRecord(run)) return run as PipelineRun;

  const normalized: PipelineRun = { ...(run as unknown as PipelineRun) };

  if (Array.isArray(run.results)) {
    normalized.results = run.results.map((result) => {
      if (!isRecord(result)) return result as PipelineRun['results'][number];
      if (!hasOwn(result, 'attachments')) {
        return result as unknown as PipelineRun['results'][number];
      }

      return {
        ...result,
        attachments: normalizeAttachmentList(result.attachments),
      } as PipelineRun['results'][number];
    });
  }

  return normalized;
};

export const normalizeInputAttachment = (
  value: PipelineInputAttachment
): PipelineInputAttachment | null => {
  const type = toStringValue(value.type).trim();
  if (!type) return null;

  const normalized: PipelineInputAttachment = {
    type,
    role: cleanString(value.role),
    scene_id: cleanString(value.scene_id),
    frame_order: normalizePositiveInteger(value.frame_order),
  };

  const assignString = (
    key:
      | 'source'
      | 'state'
      | 'url'
      | 'name'
      | 'filename'
      | 'mime_type'
      | 'media_id'
      | 'checkpoint_id'
      | 'source_checkpoint_id'
      | 'source_run_id'
      | 'generated_from_checkpoint_id'
      | 'generated_from_field',
    raw: unknown
  ) => {
    const next = cleanString(raw);
    if (next) {
      normalized[key] = next;
    }
  };

  assignString('source', value.source);
  assignString('state', value.state);
  assignString('url', value.url);
  assignString('name', value.name);
  assignString('filename', value.filename ?? value.name);
  assignString('mime_type', value.mime_type);
  assignString('media_id', value.media_id);
  assignString('checkpoint_id', value.checkpoint_id);
  assignString('source_checkpoint_id', value.source_checkpoint_id);
  assignString('source_run_id', value.source_run_id);
  assignString('generated_from_checkpoint_id', value.generated_from_checkpoint_id);
  assignString('generated_from_field', value.generated_from_field);

  const size = toNumberValue(value.size_bytes ?? value.size);
  if (size !== undefined) {
    normalized.size_bytes = size;
    normalized.size = size;
  }

  const checkpointIndex = toNumberValue(value.checkpoint_index);
  if (checkpointIndex !== undefined) {
    normalized.checkpoint_index = Math.max(0, Math.floor(checkpointIndex));
  }

  if (isRecord(value.metadata)) {
    normalized.metadata = value.metadata;
  }

  return normalized;
};

export const normalizeInputAttachments = (
  value?: PipelineInputAttachment[]
): PipelineInputAttachment[] | undefined => {
  if (!value?.length) return undefined;

  const normalized = value
    .map((attachment) => normalizeInputAttachment(attachment))
    .filter((attachment): attachment is PipelineInputAttachment => Boolean(attachment));

  return normalized.length > 0 ? normalized : undefined;
};
